import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { CategoryCountResponseDto } from "@swagger/typescript-fetch-goflyer";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { adminApiService } from "../../../utils/api";
import { toastError } from "../../../utils/pageUtil/toast";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { BRAND_COLOR, BRAND_HOVER_COLOR } from "./constants";
import { StatisticsContent } from "./StatisticsContent";

interface StatisticsModalProps {
  open: boolean;
  onClose: () => void;
  flyerItemListId?: string;
}

export const StatisticsModal: React.FC<StatisticsModalProps> = ({
  open,
  onClose,
  flyerItemListId,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState<CategoryCountResponseDto | null>(
    null,
  );

  const fetchStatistics = useCallback(async () => {
    if (!flyerItemListId) return;

    setLoading(true);
    try {
      const response =
        await adminApiService.gfFlyerItemListAdminControllerGetCategoryCount(
          flyerItemListId,
        );
      setStatistics(response);
    } catch (error) {
      console.error("Error fetching statistics:", error);
      toastError(t(messages.Error_Fetching_Statistics()));
    } finally {
      setLoading(false);
    }
  }, [flyerItemListId, t]);

  const onCloseModal = useCallback(() => {
    setLoading(true);
    setStatistics(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (open && flyerItemListId) {
      fetchStatistics();
    }
  }, [open, flyerItemListId, fetchStatistics]);

  return (
    <Dialog
      open={open}
      onClose={onCloseModal}
      maxWidth="md"
      fullWidth
      aria-labelledby="statistics-dialog-title"
      data-testid="statistics-modal"
      PaperProps={{
        sx: { borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" },
      }}
    >
      <DialogTitle
        id="statistics-dialog-title"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: "1px solid #f0f0f0",
          py: 2,
          px: 3,
        }}
      >
        <AssessmentIcon sx={{ fontSize: 22, color: BRAND_COLOR }} />
        <Typography
          variant="h6"
          component="span"
          sx={{ fontWeight: 600, color: "#1a1a1a" }}
        >
          {t(messages.Statistics())}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box
            sx={{ display: "flex", justifyContent: "center", py: 4 }}
            data-testid="statistics-modal-loader"
          >
            <CircularProgress size={32} sx={{ color: BRAND_COLOR }} />
          </Box>
        ) : statistics ? (
          <StatisticsContent statistics={statistics} />
        ) : (
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ textAlign: "center", py: 4 }}
            data-testid="statistics-no-data"
          >
            {t(messages.No_statistics_available())}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, borderTop: "1px solid #f0f0f0" }}>
        <Button
          onClick={onCloseModal}
          variant="contained"
          sx={{
            backgroundColor: BRAND_COLOR,
            color: "#fff",
            px: 3,
            py: 0.75,
            fontSize: "0.875rem",
            fontWeight: 500,
            textTransform: "none",
            borderRadius: 1,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: BRAND_HOVER_COLOR,
              boxShadow: "0 2px 4px rgba(255,128,56,0.2)",
            },
          }}
          data-testid="statistics-modal-close-button"
        >
          {t(messages.Close())}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
